export type TipoServicoPessoa = 'pessoa-fisica' | 'pessoa-juridica';

export enum ServicoDisponivelEnum {
    AREA_ATUACAO = 47,
    AVERBACAO_NACIONALIDADE = 42,
    AVERBACAO_NOME = 41,
    CANCELAR_FALECIMENTO = 37,
    CARTEIRA_PROFISSIONAL = 43,
    CEDULA_IDENTIDADE = 44,
    ESPECIALIDADES_M = 46,
    VISTO_PROVISORIO = 38,
    PRE_MEDICO = 113,
    REGIMENTO_INTERNO_CORPO_CLINICO = 87,
    COMISSAO_ETICA = 67,
    CANCELAMENTO_PJ = 88,
    VISTO_ALTERACAO_CONTRATUAL = 79,
    DADOS_GERAIS_ALTERACAO_CADASTRO = 61,
    DIRETORIA_CLINICA = 63,
    INDICACAO_DIRETOR_TECNICO = 64,
    PRE_PRESTADOR = 126,
    RENOVACAO_PJ = 86,
    SOLICITACAO_DIRETOR_TECNICO = 125,
    CANCELAMENTO_INSCRICAO = 36,
    CONSULTORIO_PRIVADO_CADASTRO = 137,
    CONSULTORIO_PRIVADO_RENOVACAO = 144,
    CONSULTORIO_PRIVADO_CANCELAMENTO = 140,
    MEDICO_PCSMO = 147,
    VISTO_PROVISORIO_FRACIONADO = 148,
    POS_GRADUACAO = 155,
    CART_EVENTO = 159
}

/**
 * O que só o código sabe sobre um serviço: o `codigo` com que ele aparece na rota
 * (`/servicos/:tipo/item/:codigo`) e no `CODIGO_SERVICO_POR_CHAVE` do registry, que aponta para
 * classes TypeScript de API.
 *
 * <p>Rótulo e setor NÃO estão aqui: vêm de `WEB_TIPO_SOLICITACAO` (`NM_SOLICITACAO` e
 * `IN_TIPO_PESSOA`), pelo `CatalogoServicosStore`. Mudar nome ou setor de serviço é alteração de dado,
 * não de código — e uma segunda cópia aqui era exatamente o que fazia as duas fontes divergirem sem
 * ninguém notar.</p>
 */
export interface ServicoDisponivelConfig {
    id: ServicoDisponivelEnum;
    codigo: string;
}

/** Os 27 serviços que o módulo sabe abrir. `servico-disponivel.enum.spec.ts` fixa esta lista. */
export const SERVICOS_NAVEGAVEIS: ServicoDisponivelConfig[] = [
    { id: ServicoDisponivelEnum.CANCELAMENTO_INSCRICAO, codigo: 'cancelamento-inscricao' },
    { id: ServicoDisponivelEnum.CANCELAR_FALECIMENTO, codigo: 'cancelamento-falecimento' },
    { id: ServicoDisponivelEnum.VISTO_PROVISORIO, codigo: 'visto-corrido' },
    { id: ServicoDisponivelEnum.AVERBACAO_NOME, codigo: 'averbacao-nome' },
    { id: ServicoDisponivelEnum.AVERBACAO_NACIONALIDADE, codigo: 'averbacao-nacionalidade' },
    { id: ServicoDisponivelEnum.CARTEIRA_PROFISSIONAL, codigo: 'solicitar-2via-carteira' },
    { id: ServicoDisponivelEnum.CEDULA_IDENTIDADE, codigo: 'solicitar-2via-cedula-identidade' },
    { id: ServicoDisponivelEnum.ESPECIALIDADES_M, codigo: 'especialidade' },
    { id: ServicoDisponivelEnum.AREA_ATUACAO, codigo: 'area-atuacao' },
    { id: ServicoDisponivelEnum.PRE_MEDICO, codigo: 'pre-medico' },
    { id: ServicoDisponivelEnum.CONSULTORIO_PRIVADO_CADASTRO, codigo: 'consultorio-privado' },
    { id: ServicoDisponivelEnum.CONSULTORIO_PRIVADO_CANCELAMENTO, codigo: 'consultorio-privado-cancelamento' },
    { id: ServicoDisponivelEnum.CONSULTORIO_PRIVADO_RENOVACAO, codigo: 'consultorio-privado-renovacao' },
    { id: ServicoDisponivelEnum.MEDICO_PCSMO, codigo: 'pcmso' },
    { id: ServicoDisponivelEnum.VISTO_PROVISORIO_FRACIONADO, codigo: 'visto-fracionado' },
    { id: ServicoDisponivelEnum.POS_GRADUACAO, codigo: 'pos' },
    { id: ServicoDisponivelEnum.DADOS_GERAIS_ALTERACAO_CADASTRO, codigo: 'alteracao-cadastral' },
    { id: ServicoDisponivelEnum.DIRETORIA_CLINICA, codigo: 'diretor-clinico' },
    { id: ServicoDisponivelEnum.INDICACAO_DIRETOR_TECNICO, codigo: 'diretor-tecnico-solicitacao-pj' },
    { id: ServicoDisponivelEnum.COMISSAO_ETICA, codigo: 'comissao-etica' },
    { id: ServicoDisponivelEnum.VISTO_ALTERACAO_CONTRATUAL, codigo: 'visto-alteracao-contratual' },
    { id: ServicoDisponivelEnum.RENOVACAO_PJ, codigo: 'renovacao-cadastral' },
    { id: ServicoDisponivelEnum.REGIMENTO_INTERNO_CORPO_CLINICO, codigo: 'regimento-interno' },
    { id: ServicoDisponivelEnum.CANCELAMENTO_PJ, codigo: 'cancelamento-pj' },
    { id: ServicoDisponivelEnum.SOLICITACAO_DIRETOR_TECNICO, codigo: 'diretor-tecnico-solicitacao-pf' },
    { id: ServicoDisponivelEnum.PRE_PRESTADOR, codigo: 'pre-prestador' },
    { id: ServicoDisponivelEnum.CART_EVENTO, codigo: 'cart-evento' }
];

const SERVICOS_POR_ID = new Map<number, ServicoDisponivelConfig>(SERVICOS_NAVEGAVEIS.map((servico) => [servico.id, servico]));
const SERVICOS_POR_CODIGO = new Map<string, ServicoDisponivelConfig>(SERVICOS_NAVEGAVEIS.map((servico) => [servico.codigo, servico]));

export function obterServicoDisponivelPorId(id?: number | null): ServicoDisponivelConfig | undefined {
    if (id == null) {
        return undefined;
    }
    return SERVICOS_POR_ID.get(id);
}

export function obterServicoDisponivelPorCodigo(codigo?: string | null): ServicoDisponivelConfig | undefined {
    if (!codigo) {
        return undefined;
    }
    return SERVICOS_POR_CODIGO.get(codigo);
}
